import { useMemo, useState, useEffect, useRef, useLayoutEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { useExchangeRate } from './useExchangeRate';
import { certificationServices, images } from '../../../utils/api';
import { TableContext } from '../../../components/shared/table/context/TableContext';
import { ROW_HEIGHT } from '../../../components/shared/table/BaseTable';

const calculateMaxBid = (maxPrice, profitMargin, buyersPremium, exchangeRate, isSameCurrency) => {
  if (!maxPrice) return null;
  
  // Target price is max price minus profit margin
  const targetPrice = maxPrice * (1 - profitMargin / 100);
  
  // Max bid accounting for buyer's premium
  const maxBid = targetPrice / (1 + buyersPremium / 100);
  
  // If currencies are the same, no conversion needed
  if (isSameCurrency) {
    return Math.round(maxBid);
  }
  
  // Otherwise convert to house currency
  if (!exchangeRate) return null;
  return Math.round(maxBid / exchangeRate);
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  // For JPY, round to whole number
  if (currency === 'JPY') {
    amount = Math.round(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2
  }).format(amount);
};

const useLotColumns = (houseCurrency = 'USD', userCurrency = 'USD', profitMargin = 0, buyersPremium = 0, onUpdateLot) => {
  const { preferences } = useContext(TableContext);
  const density = preferences?.density || 'standard';
  const [services, setServices] = useState({});
  const { data: rate } = useExchangeRate(houseCurrency, userCurrency);
  const [openStatusDropdownId, setOpenStatusDropdownId] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await certificationServices.getAll();
        const servicesMap = {};
        servicesData.forEach(service => {
          servicesMap[service.id] = service.name;
        });
        setServices(servicesMap);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    fetchServices();
  }, []);

  const handleUpdateLot = async (row, updates) => {
    await onUpdateLot({
      id: row.id,
      status: updates.status ?? row.status,
      winning_bid: updates.winning_bid ?? row.winning_bid,
      max_price: updates.max_price ?? row.max_price
    });
  };
  
  return useMemo(() => [
    {
      field: 'image',
      headerName: 'Image',
      width: ROW_HEIGHT[density],
      sortable: false,
      renderCell: (row) => {
        if (!row.imageUrl) return '-';
        return (
          <div className="flex items-center justify-center overflow-hidden h-full">
            <div 
              className="relative"
              style={{
                height: `${ROW_HEIGHT[density]}px`,
                width: `${ROW_HEIGHT[density]}px`
              }}
            >
              <img
                src={row.imageUrl}
                alt="Coin thumbnail"
                className="absolute inset-0 w-full h-full object-cover rounded"
                loading="lazy"
              />
            </div>
          </div>
        );
      }
    },
    {
      field: 'lot_number',
      headerName: 'Lot #',
      width: 100,
      renderCell: (row) => row.lot_number || '-',
      sortComparator: (a, b) => {
        // Convert to numbers for comparison, handling null/undefined
        const numA = a ? parseFloat(a) : -Infinity;
        const numB = b ? parseFloat(b) : -Infinity;
        return numA - numB;
      }
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 120,
      renderCell: (row) => row.country || '-'
    },
    {
      field: 'year',
      headerName: 'Year',
      width: 100,
      renderCell: (row) => row.year || '-'
    },
    {
      field: 'mint_mark',
      headerName: 'Mint Mark',
      width: 100,
      renderCell: (row) => row.mint_mark || '-'
    },
    {
      field: 'denomination',
      headerName: 'Denomination',
      width: 150,
      renderCell: (row) => row.denomination || '-'
    },
    {
      field: 'variety',
      headerName: 'Variety',
      width: 150,
      renderCell: (row) => row.variety || '-'
    },
    {
      field: 'certification_service_id',
      headerName: 'TPG',
      width: 100,
      renderCell: (row) => services[row.certification_service_id] || '-'
    },
    {
      field: 'certification_number',
      headerName: 'Cert #',
      width: 120,
      renderCell: (row) => row.certification_number || '-'
    },
    {
      field: 'pcgs_number',
      headerName: 'PCGS #',
      width: 120,
      renderCell: (row) => row.pcgs_number || '-'
    },
    {
      field: 'grade',
      headerName: 'Grade',
      width: 100,
      renderCell: (row) => row.grade || '-'
    },
    {
      field: 'cac_sticker',
      headerName: 'CAC',
      width: 100,
      renderCell: (row) => {
        const value = row.cac_sticker || 'none';
        const getStyledValue = () => {
          switch (value.toLowerCase()) {
            case 'green':
              return <span className="text-green-600 font-medium">Green</span>;
            case 'gold':
              return <span className="text-yellow-600 font-medium">Gold</span>;
            default:
              return <span className="text-gray-400">None</span>;
          }
        };
        return getStyledValue();
      }
    },
    {
      field: 'metal',
      headerName: 'Metal',
      width: 100,
      renderCell: (row) => row.metal || '-'
    },
    {
      field: 'composition',
      headerName: 'Composition %',
      width: 120,
      renderCell: (row) => row.composition ? `${row.composition}%` : '-'
    },
    {
      field: 'weight',
      headerName: 'Weight (g)',
      width: 100,
      renderCell: (row) => row.weight || '-'
    },
    {
      field: 'diameter',
      headerName: 'Diameter (mm)',
      width: 120,
      renderCell: (row) => row.diameter || '-'
    },
    {
      field: 'edge',
      headerName: 'Edge',
      width: 120,
      renderCell: (row) => row.edge || '-'
    },
    {
      field: 'mintage',
      headerName: 'Mintage',
      width: 120,
      renderCell: (row) => row.mintage ? new Intl.NumberFormat('en-US').format(Number(row.mintage)) : '-'
    },
    {
      field: 'population',
      headerName: 'Population',
      width: 100,
      renderCell: (row) => row.population || '-'
    },
    {
      field: 'pop_higher',
      headerName: 'Pop Higher',
      width: 100,
      renderCell: (row) => row.pop_higher || '-'
    },
    {
      field: 'price_guide_value',
      headerName: 'Price Guide',
      width: 120,
      align: 'right',
      renderCell: (row) => formatCurrency(row.price_guide_value, userCurrency)
    },
    {
      field: 'assayer',
      headerName: 'Assayer',
      width: 100,
      renderCell: (row) => row.assayer || '-'
    },
    {
      field: 'minor_variety',
      headerName: 'Minor Variety',
      width: 150,
      renderCell: (row) => row.minor_variety || '-'
    },
    {
      field: 'die_variety',
      headerName: 'Die Variety',
      width: 150,
      renderCell: (row) => row.die_variety || '-'
    },
    {
      field: 'reference_number',
      headerName: 'Reference #',
      width: 150,
      renderCell: (row) => row.reference_number || '-'
    },
    {
      field: 'max_price',
      headerName: 'Max Price',
      width: 150,
      align: 'right',
      renderCell: (row) => {
        const [isEditing, setIsEditing] = useState(false);
        const [value, setValue] = useState(row.max_price || '');
        
        // Format number with commas but no currency symbol for display in input
        const formatNumber = (num) => {
          if (!num) return '';
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(num);
        };

        // Parse formatted number back to raw number
        const parseFormattedNumber = (str) => {
          if (!str) return '';
          return str.replace(/,/g, '');
        };

        if (isEditing) {
          return (
            <input
                type="text"
                onClick={(e) => e.stopPropagation()}
                data-no-row-click
                value={formatNumber(value)}
                onChange={(e) => {
                  const rawValue = parseFormattedNumber(e.target.value);
                  if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                    setValue(rawValue);
                  }
                }}
                onBlur={async (e) => {
                  e.stopPropagation();
                  if (value !== row.max_price) {
                    try {
                      await handleUpdateLot(row, {
                        max_price: value === '' ? null : parseFloat(value)
                      });
                      setIsEditing(false);
                    } catch (error) {
                      console.error('Failed to update lot:', error);
                      // Keep editing mode on error
                    }
                  } else {
                    setIsEditing(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  } else if (e.key === 'Escape') {
                    setValue(row.max_price || '');
                    setIsEditing(false);
                  }
                }}
                className="w-full h-6 px-2 text-left border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                autoFocus
              />
          );
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="w-full cursor-text h-6 px-2 text-left border border-gray-200 rounded hover:border-gray-300 text-xs"
            data-no-row-click
          >
            {formatCurrency(row.max_price, userCurrency)}
          </button>
        );
      }
    },
    {
      field: 'max_bid',
      headerName: 'Max Bid',
      width: 150,
      align: 'right',
      renderCell: (row) => {
        const maxBid = calculateMaxBid(
          row.max_price,
          profitMargin,
          buyersPremium,
          rate,
          houseCurrency === userCurrency
        );
        return formatCurrency(maxBid, houseCurrency);
      }
    },
    {
      field: 'opening_bid',
      headerName: 'Opening Bid',
      width: 150,
      align: 'right',
      renderCell: (row) => formatCurrency(row.opening_bid, houseCurrency)
    },
    {
      field: 'winning_bid',
      headerName: 'Hammer Price',
      width: 150,
      align: 'right',
      renderCell: (row) => {
        const [isEditing, setIsEditing] = useState(false);
        const [value, setValue] = useState(row.winning_bid || '');
        
        // Format number with commas but no currency symbol for display in input
        const formatNumber = (num) => {
          if (!num) return '';
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(num);
        };

        // Parse formatted number back to raw number
        const parseFormattedNumber = (str) => {
          if (!str) return '';
          return str.replace(/,/g, '');
        };

        if (isEditing) {
          return (
            <input
                type="text"
                onClick={(e) => e.stopPropagation()}
                data-no-row-click
                value={formatNumber(value)}
                onChange={(e) => {
                  const rawValue = parseFormattedNumber(e.target.value);
                  if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                    setValue(rawValue);
                  }
                }}
                onBlur={async (e) => {
                  e.stopPropagation();
                  if (value !== row.winning_bid) {
                    try {
                      await handleUpdateLot(row, {
                        winning_bid: value === '' ? null : parseFloat(value)
                      });
                      setIsEditing(false);
                    } catch (error) {
                      console.error('Failed to update lot:', error);
                      // Keep editing mode on error
                    }
                  } else {
                    setIsEditing(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  } else if (e.key === 'Escape') {
                    setValue(row.winning_bid || '');
                    setIsEditing(false);
                  }
                }}
                className="w-full h-6 px-2 text-left border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                autoFocus
              />
          );
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="w-full cursor-text h-6 px-2 text-left border border-gray-200 rounded hover:border-gray-300 text-xs"
            data-no-row-click
          >
            {formatCurrency(row.winning_bid, houseCurrency)}
          </button>
        );
      }
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      width: 150,
      align: 'right',
      renderCell: (row) => {
        if (!['won', 'imported'].includes(row.status) || !row.winning_bid) {
          return '-';
        }
        const totalCost = row.winning_bid * (1 + buyersPremium / 100);
        return formatCurrency(totalCost, houseCurrency);
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (row) => {
        const [value, setValue] = useState(row.status || 'watching');
        const statusOptions = ['watching', 'won', 'lost'];
        const buttonRef = useRef(null);
        const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
        const isEditing = openStatusDropdownId === row.id;

        // Add effect to handle clicks outside dropdown
        useEffect(() => {
          const handleClickOutside = (event) => {
            // Close dropdown if click is outside
            if (!event.target.closest('[data-no-row-click]')) {
              setOpenStatusDropdownId(null);
            }
          };

          if (isEditing) {
            // Add listener with a slight delay to avoid immediate trigger
            const timeoutId = setTimeout(() => {
              window.addEventListener('click', handleClickOutside);
            }, 0);

            return () => {
              clearTimeout(timeoutId);
              window.removeEventListener('click', handleClickOutside);
            };
          }
        }, [isEditing]);

        useLayoutEffect(() => {
          if (isEditing && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width
            });
          }
        }, [isEditing]);

        const getStatusClass = (status) => {
          switch (status) {
            case 'watching': return 'bg-blue-100 text-blue-800';
            case 'won': return 'bg-green-100 text-green-800';
            case 'lost': return 'bg-red-100 text-red-800';
            case 'imported': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
          }
        };

        if (isEditing) {
          return (
            <>
              <button
                ref={buttonRef}
                type="button"
                className={`w-full h-6 px-2 text-left border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs ${getStatusClass(value)}`}
                data-no-row-click
              >
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </button>
              {createPortal(
                <div 
                  className="fixed z-50 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden"
                  style={{
                    top: dropdownPosition.top + 4,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                  }}
                >
                {statusOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setValue(option);
                      try {
                        await handleUpdateLot(row, {
                          status: option
                        });
                        setOpenStatusDropdownId(null);
                      } catch (error) {
                        console.error('Failed to update lot:', error);
                        // Keep dropdown open on error
                      }
                    }}
                    className={`w-full px-2 py-2 text-left text-xs ${getStatusClass(option)} cursor-pointer`}
                    data-no-row-click
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
                </div>,
                document.body
              )}
            </>
          );
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenStatusDropdownId(row.id);
            }}
            className={`w-full h-6 px-2 text-left border border-gray-200 rounded hover:border-gray-300 text-xs ${getStatusClass(value)} cursor-text`}
            data-no-row-click
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </button>
        );
      }
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      renderCell: (row) => row.notes || '-'
    }
  ], [houseCurrency, userCurrency, rate, profitMargin, buyersPremium, density, services, preferences?.density, openStatusDropdownId]);
};

const useRows = (lots = []) => {
  return useMemo(() => {
    try {
      if (!Array.isArray(lots)) return [];
      
      return lots.map(lot => ({
        ...lot,
        imageUrl: lot.images?.length && lot.images[0]?.thumbnail_path ? images.getImage(lot.images[0].thumbnail_path, lot.images[0].source) : null
      }));
    } catch (error) {
      console.error('Error processing lots:', error);
      return [];
    }
  }, [lots]);
};

export default useLotColumns;
export { useRows };
